// Admin → Clinical → Reference Library → Web search (contract §6ai).
// Configures Web Search References: on/off, the search service and its key,
// which sites results may come from, limits, the AI summary, which departments
// may use it, and the message doctors see when the service fails.
// The key is never shown: the server returns a mask, and a mask sent back is
// ignored, so saving the form never overwrites a working key.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { Switch } from '@/components/ui/Switch';
import { toast } from '@/hooks/useToast';
import { keys } from '@/lib/queryKeys';
import { getSpecialties } from '@/features/admin/api';
import {
  fetchWebSearchSettings, saveWebSearchSettings, testWebSearch,
  type WebSearchProvider, type WebSearchSettings, type WebSearchSettingsPatch,
} from '@/features/clinical-references/api';

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');
const lines = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
const showTime = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export function WebSearchSettingsView() {
  const settings = useQuery({ queryKey: keys.admin.clinicalWebSearchSettings(), queryFn: fetchWebSearchSettings });
  if (settings.isLoading) return <LoadingState />;
  if (settings.isError || !settings.data) return <ErrorState message="Couldn’t load the web search settings." onRetry={() => settings.refetch()} />;
  return <Form s={settings.data} />;
}

function Form({ s }: { s: WebSearchSettings }) {
  const qc = useQueryClient();
  const specialties = useQuery({ queryKey: keys.config.specialties(), queryFn: () => getSpecialties() });
  const [enabled, setEnabled] = useState(s.enabled);
  const [provider, setProvider] = useState<WebSearchProvider>(s.provider);
  const [apiKey, setApiKey] = useState(s.api_key);
  const [allowed, setAllowed] = useState(s.allowed_domains.join('\n'));
  const [blocked, setBlocked] = useState(s.blocked_domains.join('\n'));
  const [restrict, setRestrict] = useState(s.restrict_to_allowed);
  const [maxResults, setMaxResults] = useState(String(s.max_results));
  const [timeoutSec, setTimeoutSec] = useState(String(s.timeout_ms / 1000));
  const [rate, setRate] = useState(String(s.rate_limit_per_10min));
  const [cacheMin, setCacheMin] = useState(String(s.cache_minutes));
  const [summary, setSummary] = useState(s.summary_enabled);
  const [departments, setDepartments] = useState<number[]>(s.allowed_specialty_ids);
  const [message, setMessage] = useState(s.unavailable_message ?? '');
  const [openaiModel, setOpenaiModel] = useState(s.openai_model ?? '');
  const [lastTest, setLastTest] = useState(s.last_test);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // A fresh copy from the server (after a save) resets the form to it.
  useEffect(() => {
    setEnabled(s.enabled); setProvider(s.provider); setApiKey(s.api_key); setAllowed(s.allowed_domains.join('\n'));
    setBlocked(s.blocked_domains.join('\n')); setRestrict(s.restrict_to_allowed); setMaxResults(String(s.max_results));
    setTimeoutSec(String(s.timeout_ms / 1000)); setRate(String(s.rate_limit_per_10min)); setCacheMin(String(s.cache_minutes));
    setSummary(s.summary_enabled); setDepartments(s.allowed_specialty_ids); setMessage(s.unavailable_message ?? ''); setOpenaiModel(s.openai_model ?? ''); setLastTest(s.last_test);
  }, [s]);

  const meta = useMemo(() => s.providers.find((p) => p.id === provider), [s.providers, provider]);
  const readOnly = !s.can_manage;

  const patch = (): WebSearchSettingsPatch => ({
    enabled, provider, api_key: apiKey, allowed_domains: lines(allowed), blocked_domains: lines(blocked), restrict_to_allowed: restrict,
    max_results: Number(maxResults), timeout_ms: Math.round(Number(timeoutSec) * 1000), rate_limit_per_10min: Number(rate),
    cache_minutes: Number(cacheMin), summary_enabled: summary, allowed_specialty_ids: departments, unavailable_message: message.trim() || null,
    openai_model: openaiModel.trim() || null,
  });
  const onSaved = (fresh: WebSearchSettings) => { setFieldErrors({}); qc.setQueryData(keys.admin.clinicalWebSearchSettings(), fresh); };
  const onFailed = (e: unknown) => {
    const details = (e as { details?: unknown })?.details;
    if (details && typeof details === 'object') setFieldErrors(details as Record<string, string>);
    toast({ title: 'Not saved', description: errText(e), variant: 'danger' });
  };
  const save = useMutation({
    mutationFn: () => saveWebSearchSettings(patch()),
    onSuccess: (fresh) => { onSaved(fresh); toast({ title: 'Saved', variant: 'success' }); },
    onError: onFailed,
  });
  const test = useMutation({
    mutationFn: () => testWebSearch(patch()),
    onSuccess: (r) => { onSaved(r.settings); setLastTest(r.settings.last_test); toast({ title: r.ok ? 'Connection works' : 'Connection test failed', description: r.message, variant: r.ok ? 'success' : 'danger' }); },
    onError: onFailed,
  });
  const removeKey = useMutation({
    mutationFn: () => saveWebSearchSettings({ clear_api_key: true, ...(meta?.needs_key ? { enabled: false } : {}) }),
    onSuccess: (fresh) => { onSaved(fresh); toast({ title: 'API key removed', variant: 'success' }); },
    onError: onFailed,
  });

  const toggleDept = (id: number, on: boolean) => setDepartments((d) => (on ? [...new Set([...d, id])] : d.filter((x) => x !== id)));

  return (
    <div className="flex max-w-[720px] flex-col gap-4 text-[13px]">
      {s.warnings.length > 0 && (
        <div className="flex flex-col gap-1 rounded-lg bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">
          {s.warnings.map((w) => <div key={w} className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w}</div>)}
        </div>
      )}

      <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4">
        <label className="flex items-start justify-between gap-4">
          <span>
            <span className="block font-semibold text-ink">Web Search References</span>
            <span className="block text-[12px] text-ink-muted">
              Lets doctors search trusted medical websites from 📚 References in Consultation and the IP case sheet. Results are shown as external,
              with their links, and are never added to the hospital&rsquo;s approved library. Who may use it is also set by the role permission
              &ldquo;Web search references&rdquo;.
            </span>
          </span>
          <Switch checked={enabled} onCheckedChange={setEnabled} disabled={readOnly} aria-label="Web Search References on or off" />
        </label>
        {lastTest && (
          <div className={`flex items-start gap-1.5 rounded-md px-3 py-2 text-[12.5px] ${lastTest.ok ? 'bg-success-soft text-success-ink' : 'bg-danger-soft text-danger-ink'}`}>
            {lastTest.ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <span>Last test {showTime(lastTest.at)}: {lastTest.message}</span>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4">
        <div className="font-semibold text-ink">Search service</div>
        <FormField label="Provider" hint="A legitimate search API — websites are never scraped. Changing the provider does not change what doctors see.">
          <NativeSelect value={provider} onChange={(e) => setProvider(e.target.value as WebSearchProvider)} disabled={readOnly}>
            {s.providers.map((p) => <option key={p.id} value={p.id}>{p.label}{p.needs_key ? '' : ' — no key needed'}</option>)}
          </NativeSelect>
        </FormField>
        <FormField label={meta?.needs_key ? 'API key' : 'API key (optional)'} required={meta?.needs_key} error={fieldErrors.api_key}
          hint={<>{meta?.key_hint} Stored encrypted on the server and never shown again.{s.api_key_set ? ' A key is saved — leave this as it is to keep it.' : ''}</>}>
          <div className="flex gap-2">
            <Input type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)} disabled={readOnly}
              placeholder={s.api_key_set ? 'Saved' : 'Paste the key'} onFocus={(e) => { if (apiKey === s.api_key && s.api_key_set) e.currentTarget.select(); }} />
            {s.api_key_set && !readOnly && <Button variant="ghost" size="sm" loading={removeKey.isPending} onClick={() => removeKey.mutate()}>Remove key</Button>}
          </div>
        </FormField>
        {meta?.uses_ai && (
          <>
            <div className="rounded-md bg-info-soft px-3 py-2 text-[12.5px] text-info-ink">
              OpenAI searches the allowed sites and writes the answer itself, so this is an AI call. It uses the hospital&rsquo;s OpenAI key
              (Admin &rarr; AI) unless a key is pasted above, is unavailable while AI is switched off, and counts toward the AI budget. Only points
              citing an allowed site are shown. The &ldquo;AI summary&rdquo; switch below decides whether OpenAI&rsquo;s answer is shown, or only its sources.
            </div>
            <FormField label="OpenAI model (optional)" error={fieldErrors.openai_model}
              hint="Leave blank to use the hospital’s default OpenAI model (gpt-4o if none is set). The model must support OpenAI web search.">
              <Input value={openaiModel} onChange={(e) => setOpenaiModel(e.target.value)} disabled={readOnly} placeholder="e.g. gpt-4.1" maxLength={80} />
            </FormField>
          </>
        )}
        {meta?.fixed_domains && (
          <div className="text-[12px] text-ink-muted">{meta.label} returns results from {meta.fixed_domains.join(', ')} only.</div>
        )}
      </section>

      <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4">
        <div className="font-semibold text-ink">Trusted sites</div>
        <label className="flex items-start justify-between gap-4">
          <span>
            <span className="block text-ink">Only show results from the allowed sites</span>
            <span className="block text-[12px] text-ink-muted">Recommended. A site matches its subdomains too (nih.gov covers pubmed.ncbi.nlm.nih.gov).</span>
          </span>
          <Switch checked={restrict} onCheckedChange={setRestrict} disabled={readOnly} aria-label="Restrict to allowed sites" />
        </label>
        <FormField label="Allowed sites" error={fieldErrors.allowed_domains} hint="One per line, e.g. who.int. Used for the search itself where the provider supports it, and checked again on every result.">
          <Textarea rows={6} value={allowed} onChange={(e) => setAllowed(e.target.value)} disabled={readOnly} className="font-mono text-[12.5px]" />
        </FormField>
        <FormField label="Blocked sites" error={fieldErrors.blocked_domains} hint="Never shown, even when results are not restricted to the allowed sites.">
          <Textarea rows={3} value={blocked} onChange={(e) => setBlocked(e.target.value)} disabled={readOnly} className="font-mono text-[12.5px]" />
        </FormField>
      </section>

      <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4">
        <div className="font-semibold text-ink">Results and limits</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Results shown" error={fieldErrors.max_results} hint="1–10 sources per search">
            <Input type="number" min={1} max={10} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} disabled={readOnly} />
          </FormField>
          <FormField label="Timeout (seconds)" error={fieldErrors.timeout_ms} hint={`2–30. Doctors see the message below if the service is slower.${meta?.min_timeout_ms ? ` ${meta.label} writes the answer too, so it always gets at least ${meta.min_timeout_ms / 1000} seconds.` : ''}`}>
            <Input type="number" min={2} max={30} value={timeoutSec} onChange={(e) => setTimeoutSec(e.target.value)} disabled={readOnly} />
          </FormField>
          <FormField label="Searches per doctor per 10 minutes" error={fieldErrors.rate_limit_per_10min} hint="1–200. Keeps usage and cost in check.">
            <Input type="number" min={1} max={200} value={rate} onChange={(e) => setRate(e.target.value)} disabled={readOnly} />
          </FormField>
          <FormField label="Reuse identical results for (minutes)" error={fieldErrors.cache_minutes} hint="0–1440. 0 always searches afresh.">
            <Input type="number" min={0} max={1440} value={cacheMin} onChange={(e) => setCacheMin(e.target.value)} disabled={readOnly} />
          </FormField>
        </div>
        <label className="flex items-start justify-between gap-4">
          <span>
            <span className="block text-ink">AI summary of the results</span>
            <span className="block text-[12px] text-ink-muted">
              A few points written only from the result snippets, each citing its source; anything uncited is removed. Needs AI switched on for the
              hospital. Only the question and the public snippets are sent to the AI.
            </span>
          </span>
          <Switch checked={summary} onCheckedChange={setSummary} disabled={readOnly} aria-label="AI summary on or off" />
        </label>
        <FormField label="Message when the search service fails" error={fieldErrors.unavailable_message} hint="Optional. Leave blank for the built-in wording (e.g. “The web search service is unavailable right now. Try again in a moment.”).">
          <Textarea rows={2} maxLength={300} value={message} onChange={(e) => setMessage(e.target.value)} disabled={readOnly} />
        </FormField>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
        <div>
          <div className="font-semibold text-ink">Departments</div>
          <div className="text-[12px] text-ink-muted">
            Leave all unticked to allow every department. When any are ticked, only staff assigned to those departments (Users &amp; Roles → specialty) can search.
          </div>
        </div>
        {fieldErrors.allowed_specialty_ids && <div className="text-[12px] text-danger-ink">{fieldErrors.allowed_specialty_ids}</div>}
        {specialties.isLoading && <div className="text-[12px] text-ink-muted">Loading departments…</div>}
        {specialties.data?.length === 0 && <div className="text-[12px] text-ink-muted">No departments are set up yet (Admin → Clinical → Specialties).</div>}
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {specialties.data?.map((sp) => (
            <label key={sp.id} className="flex items-center gap-2 text-ink">
              <Checkbox checked={departments.includes(sp.id)} onCheckedChange={(v) => toggleDept(sp.id, v === true)} disabled={readOnly} />
              {sp.name}
            </label>
          ))}
        </div>
      </section>

      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={test.isPending}>Save</Button>
          <Button variant="ghost" onClick={() => test.mutate()} loading={test.isPending} disabled={save.isPending}>Save &amp; test connection</Button>
        </div>
      )}
      {s.updated_by && <div className="text-[11.5px] text-ink-muted">Last changed {showTime(s.updated_at)} by {s.updated_by}</div>}
    </div>
  );
}
